// BackgroundControl.js with toggle functionality
import React, { useState, useEffect } from 'react';
import './BackgroundControl.css';
import BackgroundManager from './BackgroundManager';

//const _host = 'chappie-demo.novosense.africa';
const _host = 'chappie-demo.novosense.africa';
const _company = 'wdsf';

const BackgroundControl = () => {
  const [selectedBackground, setSelectedBackground] = useState(null);
  const [showIdleDisplay, setShowIdleDisplay] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch current settings on mount
  useEffect(() => {
    fetchCurrentSettings();
  }, []);

  const fetchCurrentSettings = async () => {
    try {
      const response = await fetch(`https://${_host}:6003/api/${_company}/display`);
      if (response.ok) {
        const data = await response.json();
        
        // Get background ID - check both field name variants
        const backgroundId = data.backgroundId || data.background_id;

        // Get showIdleDisplay state - check both field name variants
        const showIdleDisplayValue = data.showIdleDisplay !== undefined ? data.showIdleDisplay : data.show_idle_display;

        if (backgroundId) {
                    // Find the background details
          const backgroundsResponse = await fetch(`https://${_host}:6003/api/${_company}/backgrounds`);
          if (backgroundsResponse.ok) {
            const backgrounds = await backgroundsResponse.json();
            const background = backgrounds.find(bg => bg.id === backgroundId);
            if (background) {
                            setSelectedBackground(background);
            }
          }
        }

        // Update toggle state if backend provides it
        if (showIdleDisplayValue !== undefined) {
                    setShowIdleDisplay(showIdleDisplayValue);
        }
      }
    } catch (err) {
      console.error('Error fetching current display settings:', err);
    }
  };

  const handleBackgroundSelected = async (background) => {
    setLoading(true);
    setError(null);

    try {
      
      // Get current display data to preserve existing values
      const response = await fetch(`https://${_host}:6003/api/${_company}/display`);
      let currentDisplayData = {};
      if (response.ok) {
        currentDisplayData = await response.json() || {};
      }

      // Only send the specific fields that should be updated (not the entire display object)
      const updatedDisplayData = {
        // Only include fields that should be updated
        backgroundId: background ? background.id : null,
        background_id: background ? background.id : null, // Include snake_case for compatibility
        backgroundImage: background ? background.url : null,
        background_image: background ? background.url : null, // Include snake_case for compatibility
        backgroundType: background ? background.type : null,
        background_type: background ? background.type : null // Include snake_case for compatibility
        // Don't include showIdleDisplay - let it remain as-is in the backend
      };

      
      const updateResponse = await fetch('https://'+_host+':6003/api/'+_company+'/display', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedDisplayData)
      });

      
      if (!updateResponse.ok) {
        console.error('🎬 BackgroundControl - API error response:', await updateResponse.text());
        throw new Error('Failed to update background');
      }

      const responseData = await updateResponse.json();
      
      setSelectedBackground(background);
      setSuccess(`Background ${background ? 'updated' : 'cleared'} successfully!`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating background:', err);
      setError(err.message || 'Failed to update background');
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleIdleDisplay = async (value) => {
    setLoading(true);
    setError(null);

    try {
      
      // Get current display data to preserve existing settings
      const response = await fetch('https://'+_host+':6003/api/'+_company+'/display');
      let currentDisplayData = {};
      if (response.ok) {
        currentDisplayData = await response.json() || {};
      }

      
      // Only send the specific fields that should be updated
      const updatedDisplayData = {
        showIdleDisplay: value,
        show_idle_display: value, // Include snake_case for backend compatibility
        // Preserve current background settings
        backgroundId: currentDisplayData.background_id || null,
        background_id: currentDisplayData.background_id || null
      };

      
      const updateResponse = await fetch('https://'+_host+':6003/api/'+_company+'/display', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedDisplayData)
      });

      
      if (!updateResponse.ok) {
        console.error('🎬 BackgroundControl - API error response:', await updateResponse.text());
        throw new Error('Failed to update display settings');
      }

      const responseData = await updateResponse.json();
      
      // Update component state immediately for immediate UI feedback
      setShowIdleDisplay(value);

      // Show success message with visual feedback
      const action = value ? 'enabled' : 'disabled';
      setSuccess(`Background-only mode ${action} successfully!`);
      setTimeout(() => setSuccess(null), 3000);

      // Trigger immediate polling refresh in DisplayScreen by sending a custom event
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('displayDataUpdated', {
          detail: {
            timestamp: Date.now(),
            action: 'toggleIdleDisplay',
            value: value
          }
        }));
              }, 200);
    } catch (err) {
      console.error('Error updating idle display setting:', err);
      setError(err.message || 'Failed to update settings');
      setTimeout(() => setError(null), 3000);

      // Revert the UI state if the API call failed
      setShowIdleDisplay(!value); // Revert to previous state
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="background-control-container">
      <h2>Display Background Control</h2>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="background-control-info">
        <p>
          Upload and manage background images and videos for your dance competition display.
          When "Show background ONLY (No Heats displayed)" is enabled, your selected background will be displayed
          even when no competition is running.
        </p>
      </div>

      <BackgroundManager
        onBackgroundSelected={handleBackgroundSelected}
        currentBackground={selectedBackground}
        onToggleIdleDisplay={handleToggleIdleDisplay}
        showIdleDisplay={showIdleDisplay}
      />
    </div>
  );
};

export default BackgroundControl;